import { spawnSync } from 'node:child_process';

const isWindows = process.platform === 'win32';

export interface ExecOptions {
  cwd: string;
  /** Extra env merged over process.env (e.g. DATABASE_URL). */
  env?: Record<string, string>;
  /**
   * .cmd shims (npm/npx on Windows) can only be spawned through a shell
   * since CVE-2024-27980. Command + args are compile-time constants here, so
   * shell:true carries no injection surface. Callers set this for npx.
   */
  needsShell?: boolean;
}

export interface ExecResult {
  status: number;
  stdout: string;
  stderr: string;
  /** ENOENT etc. — the binary couldn't be launched at all. */
  spawnError?: string;
}

/**
 * Run a child process to completion, capturing stdout/stderr. Synchronous +
 * fully mockable from tests (vi.mock('node:child_process')). Never throws;
 * a missing binary comes back as { status: -1, spawnError }.
 */
export function runChild(cmd: string, args: string[], options: ExecOptions): ExecResult {
  const res = spawnSync(cmd, args, {
    cwd: options.cwd,
    encoding: 'utf8',
    shell: options.needsShell === true,
    env: { ...process.env, ...(options.env ?? {}) },
    // Some tools (prisma) probe for a TTY; a non-TTY pipe is fine and keeps
    // them non-interactive, which is what we want for an agent-driven CLI.
    stdio: ['ignore', 'pipe', 'pipe'],
  });
  if (res.error) {
    return {
      status: -1,
      stdout: res.stdout ?? '',
      stderr: res.stderr ?? '',
      spawnError: (res.error as NodeJS.ErrnoException).message,
    };
  }
  return {
    status: res.status ?? -1,
    stdout: res.stdout ?? '',
    stderr: res.stderr ?? '',
  };
}

/** `npx` is npx.cmd on Windows — needs a shell, like npm. */
export function npxCommand(): { cmd: string; needsShell: boolean } {
  return { cmd: 'npx', needsShell: isWindows };
}

/** The python interpreter name per platform (python3 on POSIX, python on Windows). */
export function pythonCommand(): string {
  return isWindows ? 'python' : 'python3';
}
